import React, { useState, useEffect } from "react";
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Form,
  Modal,
} from "react-bootstrap";
import {
  FaUsers,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEdit,
  FaEye,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaUserCheck,
  FaUserTimes,
  FaShieldAlt,
} from "react-icons/fa";
import { FiActivity } from "react-icons/fi";
import apiService from "../../services/api"

export default function AdminPatients() {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ✅ Fetch patients on mount
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const patientsResponse = await apiService.getPatients(1, 100);
        setPatients(patientsResponse.data.patients || []);
      } catch (err) {
        console.error("Error fetching patients:", err);
      }
    };
    fetchPatients();
  }, []);

  // ✅ Filter patients
  const filteredPatients = patients.filter((patient) => {
    const matchesSearch =
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm);

    // API doesn’t send "Active"/"Inactive", so derive status
    const status =
      patient.total_appointments > 0 ? "Active" : "Inactive";

    const matchesStatus =
      statusFilter === "all" ||
      status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const activePatients = patients.filter(
    (p) => p.total_appointments > 0
  ).length;
  const inactivePatients = patients.filter(
    (p) => p.total_appointments === 0
  ).length;

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: "#f8fffe" }}>
      <Container className="py-5">
        {/* Header */}
        <div className="mb-5">
          <div
            className="p-5 rounded-4 text-white position-relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
            }}
          >
            <Row className="align-items-center position-relative">
              <Col md={8}>
                <div className="mb-3">
                  <Badge
                    className="px-3 py-2 rounded-pill fw-medium mb-3"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <FaShieldAlt className="me-2" />
                    Patient Management
                  </Badge>
                </div>
                <h1 className="display-5 fw-bold mb-3">
                  Patient Database
                </h1>
                <p className="fs-5 opacity-90 mb-4">
                  Manage patient records and information
                </p>
                {/* <Button
                  variant="light"
                  className="px-4 py-2 rounded-pill fw-medium"
                  style={{
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <FaPlus className="me-2" style={{ color: "#0891b2" }} />
                  <span style={{ color: "#0891b2" }}>
                    Add New Patient
                  </span>
                </Button> */}
              </Col>
              <Col md={4} className="text-end">
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "120px",
                    height: "120px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <FaUsers size={48} />
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-5">
          <Col md={3}>
            <Card className="border-0 rounded-4 h-100">
              <Card.Body className="p-4 text-white text-center"
                style={{
                  background:
                    "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                }}
              >
                <FaUsers size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{patients.length}</div>
                <div className="opacity-90">Total Patients</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 rounded-4 h-100">
              <Card.Body className="p-4 text-white text-center"
                style={{
                  background:
                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                }}
              >
                <FaUserCheck size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{activePatients}</div>
                <div className="opacity-90">Active Patients</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 rounded-4 h-100">
              <Card.Body className="p-4 text-white text-center"
                style={{
                  background:
                    "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                }}
              >
                <FaUserTimes size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">{inactivePatients}</div>
                <div className="opacity-90">Inactive Patients</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 rounded-4 h-100">
              <Card.Body className="p-4 text-white text-center"
                style={{
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                }}
              >
                <FiActivity size={32} className="mb-3" />
                <div className="h3 fw-bold mb-1">
                  {patients.reduce(
                    (sum, p) => sum + (p.total_appointments || 0),
                    0
                  )}
                </div>
                <div className="opacity-90">Total Appointments</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="border-0 rounded-4 mb-4">
          <Card.Body className="p-4">
            <Row className="g-3">
              <Col md={8}>
                <div className="position-relative">
                  <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                  <Form.Control
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ps-5 rounded-3 border-0"
                  />
                </div>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-3 border-0"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Patients Table */}
        <Card className="border-0 rounded-4">
          <Card.Body className="p-0">
            {filteredPatients.length > 0 ? (
              <div className="d-flex flex-column">
                {filteredPatients.map((patient, index) => (
                  <div
                    key={patient.id}
                    className={`p-4 d-flex justify-content-between align-items-center ${
                      index !== filteredPatients.length - 1
                        ? "border-bottom"
                        : ""
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle me-4 d-flex align-items-center justify-content-center"
                        style={{
                          width: "60px",
                          height: "60px",
                          background:
                            "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                          color: "white",
                        }}
                      >
                        <FaUsers size={24} />
                      </div>
                      <div>
                        <div className="fw-bold mb-1">
                          {patient.name}
                        </div>
                        <div className="d-flex align-items-center text-muted small mb-1">
                          <FaEnvelope className="me-1" size={12} />
                          {patient.email}
                        </div>
                        <div className="d-flex align-items-center text-muted small mb-1">
                          <FaPhone className="me-1" size={12} />
                          {patient.phone}
                        </div>
                        <div className="d-flex align-items-center text-muted small">
                          <FaCalendarAlt className="me-1" size={12} />
                          Age: {calculateAge(patient.date_of_birth)}
                        </div>
                      </div>
                    </div>
                    <div className="text-end">
                      <Badge
                        className="mb-2"
                        style={{
                          background:
                            patient.total_appointments > 0
                              ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                              : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                        }}
                      >
                        {patient.total_appointments > 0
                          ? "Active"
                          : "Inactive"}
                      </Badge>
                      <div className="text-muted small mb-2">
                        {patient.total_appointments} appointments
                      </div>
                      <div className="text-muted small mb-3">
                        Created:{" "}
                        {new Date(
                          patient.created_at
                        ).toLocaleDateString()}
                      </div>
                      <div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="rounded-pill"
                          onClick={() => handleViewDetails(patient)}
                        >
                          <FaEye className="me-1" size={12} />
                          View
                        </Button>
                        {/* <Button
                          size="sm"
                          variant="outline-success"
                          className="rounded-pill"
                        >
                          <FaEdit className="me-1" size={12} />
                          Edit
                        </Button> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <FaUsers size={64} className="text-muted mb-3" />
                <h5 className="text-muted mb-2">No patients found</h5>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Patient Details Modal */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Patient Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPatient && (
              <Row className="g-4">
                <Col md={6}>
                  <h6>Personal Information</h6>
                  <div><strong>Name:</strong> {selectedPatient.name}</div>
                  <div><strong>Age:</strong> {calculateAge(selectedPatient.date_of_birth)}</div>
                  <div><strong>Email:</strong> {selectedPatient.email}</div>
                  <div><strong>Phone:</strong> {selectedPatient.phone}</div>
                  <div><strong>Status:</strong> {selectedPatient.total_appointments > 0 ? "Active" : "Inactive"}</div>
                </Col>
                <Col md={6}>
                  <h6>Appointments</h6>
                  <div><strong>Total:</strong> {selectedPatient.total_appointments}</div>
                  <div><strong>Completed:</strong> {selectedPatient.completed_appointments}</div>
                  <div><strong>Created:</strong> {new Date(selectedPatient.created_at).toLocaleString()}</div>
                </Col>
              </Row>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
}
